import React, { useMemo } from "react";
import ProfileScreen from "../../screens/user/ProfileScreen";
import VStack from "../../ui_atoms/VStack";
import { MockedProvider } from "@apollo/client/testing";
import type { Meta, StoryObj } from "@storybook/react";
import { createStackNavigator } from "@react-navigation/stack";
import { MockedAuthManager } from "../../contexts/AuthManager";
import {
    buildClient,
    buildClientProfile,
    buildIjmGroup,
    buildUser,
    buildWorkerProfile,
    IJM_CHOICES_MOCK,
} from "../factories";
import { EducationLevel, ProfileType, WorkingPermit } from "../../__generated__/graphql";
import WorkerProfileEditorScreen from "../../screens/worker/WorkerProfileEditor";
import DiplomaAndTrainingEditorScreen from "../../screens/profile-editor/DiplomaAndTrainingEditor";
import { useI18n } from "../../hooks/i18n-hooks";
import { UPDATE_CLIENT_PROFILE, UPDATE_WORKER_PROFILE } from "../../graphql/core";
import ClientProfileEditorScreen from "../../screens/client/ClientProfileEditor";
import BankAccountEditorScreen from "../../screens/profile-editor/BankAccountEditor";

import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import AccountEditorScreen from "../../screens/user/AccountEditor";
import PrivateClientEditorScreen from "../../screens/user/PrivateClientEditor";
import IJMEditorNavScreen from "../../screens/client/IJMEditorNavScreen";

const Stack = createStackNavigator<{
    Profile: undefined;
    WorkerProfileEdit: undefined;
    DiplomaAndTrainingEdit: undefined;
    BankAccountEdit: undefined;
    ClientProfileEdit: undefined;
    AccountEdit: undefined;
    PrivateClientEdit: undefined;
    IJMEditorNavScreen: undefined;
}>();

type FullProfileType = ProfileType.Worker | ProfileType.Client | "private_client";

const StoryComponent = ({ fullProfileType }: { fullProfileType: FullProfileType }) => {
    const i18n = useI18n();
    const [profileType, isPrivate] =
        fullProfileType === ProfileType.Worker
            ? [ProfileType.Worker, false]
            : fullProfileType === "private_client"
              ? [ProfileType.Client, true]
              : [ProfileType.Client, false];

    const user = useMemo(
        () =>
            buildUser({
                workerProfile:
                    profileType == ProfileType.Worker
                        ? buildWorkerProfile({
                              id: "worker",
                              educationLevel: EducationLevel.HesHep,
                          })
                        : undefined,
                clientProfiles:
                    profileType == ProfileType.Client
                        ? [
                              buildClientProfile({
                                  id: "client",
                                  isPrivateClient: isPrivate,
                                  client: buildClient({
                                      canChangeIjmGroup: true,
                                      ijmGroup: buildIjmGroup({
                                          id: "GM-G1-730-2",
                                          name: "2 jours de délai d'attente",
                                      }),
                                      ijmPayFirstDays: true,
                                      ijmPayRemainingPercents: 20,
                                  }),
                              }),
                          ]
                        : [],
            }),
        [isPrivate, profileType],
    );
    const mocks = useMemo(
        () => [
            IJM_CHOICES_MOCK,
            {
                variableMatcher: () => true,
                maxUsageCount: Number.POSITIVE_INFINITY,
                request: {
                    query: UPDATE_WORKER_PROFILE,
                },
                result: () => ({
                    data: {
                        updateWorkerProfile: buildWorkerProfile({
                            children: [],
                            hasFiscalAttestation: false,
                            workPermit: WorkingPermit.PermitOther,
                            educationLevel: EducationLevel.Baccalaureate,
                        }),
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: UPDATE_CLIENT_PROFILE,
                },
                result: () => ({
                    data: {
                        updateClientProfile: buildClientProfile({}),
                    },
                }),
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType} profileId={profileType}>
                <MockedProvider mocks={mocks}>
                    <VStack
                        gap={1}
                        style={{ alignItems: "stretch", alignSelf: "stretch", flex: 1 }}
                    >
                        <Stack.Navigator initialRouteName="Profile">
                            <Stack.Screen
                                name="Profile"
                                component={ProfileScreen}
                                options={{ headerShown: false }}
                            />
                            <Stack.Screen
                                name="WorkerProfileEdit"
                                component={WorkerProfileEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("user.actions.edit_user"),
                                }}
                            />
                            <Stack.Screen
                                name="ClientProfileEdit"
                                component={ClientProfileEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("user.actions.edit_user"),
                                }}
                            />
                            <Stack.Screen
                                name="BankAccountEdit"
                                component={BankAccountEditorScreen}
                            />
                            <Stack.Screen
                                name="DiplomaAndTrainingEdit"
                                component={DiplomaAndTrainingEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t(
                                        "screens.profile_editor.diploma_and_training.title",
                                    ),
                                }}
                            />
                            <Stack.Screen
                                name="AccountEdit"
                                component={AccountEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("screens.account_edit.title"),
                                }}
                            />
                            <Stack.Screen
                                name="PrivateClientEdit"
                                component={PrivateClientEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("screens.private_client_edit.title"),
                                }}
                            />
                            <Stack.Screen
                                name="IJMEditorNavScreen"
                                component={IJMEditorNavScreen}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: true,
                                    animation: "slide_from_bottom",
                                }}
                            />
                        </Stack.Navigator>
                    </VStack>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Profile screen",
    component: StoryComponent,
    argTypes: {
        fullProfileType: {
            options: [ProfileType.Worker, ProfileType.Client, "private_client"],
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        fullProfileType: ProfileType.Worker,
    },
};

export const Client: Story = {
    args: {
        fullProfileType: ProfileType.Client,
    },
};

export const PrivateClient: Story = {
    args: {
        fullProfileType: "private_client",
    },
};
